/**
 * Delete confirmation dialog for shifts
 * Shows shift summary and requires confirmation before deletion
 */

import { useLingui } from '@lingui/react';
import { formatDateLong } from '@/lib/format';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { AlertTriangle } from 'lucide-react';
import { ShiftTypeBadge } from './ShiftTypeBadge';
import { ShiftStatusBadge } from './ShiftStatusBadge';
import { useDeleteShift } from '@/hooks/useShifts';
import type { Shift } from '@/types/shifts';

export interface DeleteShiftDialogProps {
  shift: Shift;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

/**
 * Alert dialog component for confirming shift deletion.
 * Shows a summary of the shift to be deleted and requires user confirmation.
 */
export function DeleteShiftDialog({
  shift,
  open,
  onOpenChange,
  onSuccess,
}: DeleteShiftDialogProps) {
  const { i18n } = useLingui();
  const deleteShift = useDeleteShift();

  // Format the shift date for display
  const formattedDate = shift.shift_date
    ? formatDateLong(shift.shift_date, i18n.locale)
    : '';

  // Get employee display name
  const employeeName = shift.employee
    ? `${shift.employee.firstname} ${shift.employee.lastname}`
    : i18n._({ id: 'common.unknown', message: 'Unknown' });

  // Resolves on success so the confirm closes; a failure rejects (the hook
  // toasts) and the dialog stays open for a retry.
  const handleDelete = async () => {
    if (!shift.id) {
      throw new Error('DeleteShiftDialog: shift.id is missing');
    }
    await deleteShift.mutateAsync(shift.id);
    onSuccess?.();
  };

  return (
    <ConfirmDialog
      open={open}
      onOpenChange={onOpenChange}
      title={i18n._({ id: 'shifts.delete.title', message: 'Delete Shift' })}
      description={i18n._({
        id: 'shifts.delete.confirmation',
        message: 'Are you sure you want to delete this shift?',
      })}
      confirmLabel={i18n._({ id: 'common.delete', message: 'Delete' })}
      variant="destructive"
      onConfirm={handleDelete}
      isPending={deleteShift.isPending}
    >
      <div className="space-y-3">
        {/* Shift Summary */}
        <div className="rounded-md border p-3 bg-muted/50 space-y-2">
          <div className="font-medium">{employeeName}</div>
          <div className="text-sm text-muted-foreground">{formattedDate}</div>
          <div className="text-sm text-muted-foreground">
            {shift.start_time} - {shift.end_time}
          </div>
          <div className="flex items-center gap-2 pt-1">
            {shift.shift_type && <ShiftTypeBadge type={shift.shift_type} />}
            {shift.status && <ShiftStatusBadge status={shift.status} />}
          </div>
        </div>

        {/* Warning for published/confirmed shifts */}
        {shift.status !== 'DRAFT' && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" aria-hidden="true" />
            <AlertTitle>
              {i18n._({
                id: 'shifts.delete.publishedWarning.title',
                message: 'Published Shift',
              })}
            </AlertTitle>
            <AlertDescription>
              {i18n._({
                id: 'shifts.delete.publishedWarning.message',
                message:
                  'Deleting a published shift will remove it from employees\' schedules.',
              })}
            </AlertDescription>
          </Alert>
        )}
      </div>
    </ConfirmDialog>
  );
}
